import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { PingPongSpot } from './map';
import { TABLE_H, isPongSnapshot, type PongSnapshot } from './pong';

/**
 * Matchmaking for the ping pong table.
 *
 * Single player is local. Two players advertise on the office realtime
 * channel while they stand at the same table; the lower user id hosts
 * the rally and the other sends paddle position.
 */

export type PongSessionPhase = 'idle' | 'waiting' | 'single' | 'host' | 'guest';
export type PongLink = 'idle' | 'connecting' | 'live' | 'lost';
export type PongLostReason = 'left' | 'timeout' | null;

interface Seeker {
  userId: string;
  name: string;
  tableId: string;
  receivedAt: number;
}

interface MatchInfo {
  matchId: string;
  tableId: string;
  hostId: string;
  guestId: string;
  hostName: string;
  guestName: string;
}

interface StartPayload {
  kind: 'start';
  tableId: string;
  matchId: string;
  hostId: string;
  guestId: string;
  hostName: string;
  guestName: string;
}

export interface PongSession {
  phase: PongSessionPhase;
  link: PongLink;
  lostReason: PongLostReason;
  /** True while a match or solo game should freeze walking. */
  blocking: boolean;
  near: PingPongSpot | null;
  setNear: (spot: PingPongSpot | null) => void;
  /** Coworkers currently waiting at the table the player is standing next to. */
  waitingHere: string[];
  opponentName: string | null;
  leftName: string;
  rightName: string;
  youSide: 'left' | 'right';
  guestYRef: MutableRefObject<number | null>;
  snapRef: MutableRefObject<PongSnapshot | null>;
  rematchNonce: number;
  startSingle: () => void;
  startDual: () => void;
  close: () => void;
  playSoloInstead: () => void;
  keepWaiting: () => void;
  publish: (snap: PongSnapshot) => void;
  sendPaddle: (y: number) => void;
  requestRematch: () => void;
}

interface UsePongSessionArgs {
  userId: string | null | undefined;
  name: string;
  connected: boolean;
  sendPong: (payload: unknown) => void;
  subscribePong: (fn: (payload: unknown) => void) => () => void;
  sendEmote?: (text: string) => void;
}

const SEEK_TTL_MS = 2200;
const CONNECT_TIMEOUT_MS = 8000;
const LIVE_TIMEOUT_MS = 12000;

function cleanName(v: unknown, fallback = 'Coworker'): string {
  if (typeof v !== 'string') return fallback;
  const s = v.replace(/\s+/g, ' ').trim().slice(0, 80);
  return s || fallback;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object') return null;
  return v as Record<string, unknown>;
}

export function usePongSession(args: UsePongSessionArgs): PongSession {
  const { userId, name, connected, sendPong, subscribePong } = args;
  const [phase, setPhase] = useState<PongSessionPhase>('idle');
  const [link, setLink] = useState<PongLink>('idle');
  const [lostReason, setLostReason] = useState<PongLostReason>(null);
  const [near, setNearState] = useState<PingPongSpot | null>(null);
  const [seekers, setSeekers] = useState<Seeker[]>([]);
  const [opponentName, setOpponentName] = useState<string | null>(null);
  const [rematchNonce, setRematchNonce] = useState(0);

  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const linkRef = useRef(link);
  linkRef.current = link;
  const nearRef = useRef(near);
  nearRef.current = near;
  const userIdRef = useRef(userId ?? null);
  userIdRef.current = userId ?? null;
  const nameRef = useRef(name);
  nameRef.current = name;
  const sendRef = useRef(sendPong);
  sendRef.current = sendPong;
  const emoteRef = useRef(args.sendEmote);
  emoteRef.current = args.sendEmote;

  const matchRef = useRef<MatchInfo | null>(null);
  const waitingTableRef = useRef<string | null>(null);
  const offerRef = useRef<string | null>(null);
  const startPayloadRef = useRef<StartPayload | null>(null);
  const guestYRef = useRef<number | null>(null);
  const snapRef = useRef<PongSnapshot | null>(null);
  const lastPacketRef = useRef(0);
  const celebratedRef = useRef(false);

  const setNear = useCallback((spot: PingPongSpot | null) => {
    setNearState((prev) => (prev?.id === spot?.id ? prev : spot));
  }, []);

  const celebrate = () => {
    if (celebratedRef.current) return;
    celebratedRef.current = true;
    emoteRef.current?.('🏓');
  };

  const resetMatch = () => {
    matchRef.current = null;
    startPayloadRef.current = null;
    offerRef.current = null;
    guestYRef.current = null;
    snapRef.current = null;
    celebratedRef.current = false;
  };

  const close = useCallback(() => {
    const id = userIdRef.current;
    const match = matchRef.current;
    const current = phaseRef.current;
    if (id && match && (current === 'host' || current === 'guest')) {
      sendRef.current({ kind: 'leave', matchId: match.matchId, userId: id });
    }
    if (id && current === 'waiting' && waitingTableRef.current) {
      sendRef.current({ kind: 'cancel', tableId: waitingTableRef.current, userId: id });
    }
    waitingTableRef.current = null;
    resetMatch();
    setOpponentName(null);
    setLostReason(null);
    setLink('idle');
    setPhase('idle');
  }, []);

  const startSingle = useCallback(() => {
    if (!nearRef.current) return;
    waitingTableRef.current = null;
    resetMatch();
    setOpponentName(null);
    setLostReason(null);
    setLink('live');
    setPhase('single');
    celebrate();
  }, []);

  const startDual = useCallback(() => {
    const spot = nearRef.current;
    const id = userIdRef.current;
    if (!spot || !id || !connected) return;
    resetMatch();
    waitingTableRef.current = spot.id;
    setOpponentName(null);
    setLostReason(null);
    setLink('idle');
    setPhase('waiting');
  }, [connected]);

  const playSoloInstead = useCallback(() => {
    const id = userIdRef.current;
    const match = matchRef.current;
    if (id && match) sendRef.current({ kind: 'leave', matchId: match.matchId, userId: id });
    waitingTableRef.current = null;
    resetMatch();
    setOpponentName(null);
    setLostReason(null);
    setLink('live');
    setPhase('single');
    celebrate();
  }, []);

  const keepWaiting = useCallback(() => {
    const id = userIdRef.current;
    const match = matchRef.current;
    const tableId = match?.tableId ?? waitingTableRef.current ?? nearRef.current?.id ?? null;
    if (id && match) sendRef.current({ kind: 'leave', matchId: match.matchId, userId: id });
    resetMatch();
    setOpponentName(null);
    setLostReason(null);
    setLink('idle');
    if (tableId && nearRef.current?.id === tableId) {
      waitingTableRef.current = tableId;
      setPhase('waiting');
    } else {
      waitingTableRef.current = null;
      setPhase('idle');
    }
  }, []);

  const publish = useCallback((snap: PongSnapshot) => {
    const match = matchRef.current;
    const id = userIdRef.current;
    if (!match || !id || phaseRef.current !== 'host') return;
    sendRef.current({ kind: 'state', matchId: match.matchId, userId: id, snap });
  }, []);

  const sendPaddle = useCallback((y: number) => {
    const match = matchRef.current;
    const id = userIdRef.current;
    if (!match || !id || phaseRef.current !== 'guest') return;
    if (!Number.isFinite(y)) return;
    sendRef.current({ kind: 'input', matchId: match.matchId, userId: id, y });
  }, []);

  const requestRematch = useCallback(() => {
    const match = matchRef.current;
    const id = userIdRef.current;
    if (!match || !id || phaseRef.current !== 'guest') return;
    sendRef.current({ kind: 'rematch', matchId: match.matchId, userId: id });
  }, []);

  // Incoming game messages.
  useEffect(() => {
    return subscribePong((raw) => {
      const msg = asRecord(raw);
      const kind = msg && typeof msg.kind === 'string' ? msg.kind : '';
      const self = userIdRef.current;
      const from = msg && typeof msg.userId === 'string' ? msg.userId : '';
      if (!kind || !from || from === self) return;

      if (kind === 'seek') {
        const tableId = typeof msg!.tableId === 'string' ? msg!.tableId : '';
        if (!tableId) return;
        const seeker: Seeker = {
          userId: from,
          name: cleanName(msg!.name),
          tableId,
          receivedAt: performance.now(),
        };
        setSeekers((prev) => [...prev.filter((s) => s.userId !== from), seeker]);
        return;
      }

      if (kind === 'cancel') {
        setSeekers((prev) => prev.filter((s) => s.userId !== from));
        return;
      }

      if (kind === 'start') {
        if (phaseRef.current !== 'waiting') return;
        if (msg!.guestId !== self) return;
        if (msg!.tableId !== waitingTableRef.current) return;
        if (typeof msg!.matchId !== 'string' || typeof msg!.hostId !== 'string') return;
        if (typeof msg!.tableId !== 'string') return;
        const match: MatchInfo = {
          matchId: msg!.matchId,
          tableId: msg!.tableId,
          hostId: msg!.hostId,
          guestId: self!,
          hostName: cleanName(msg!.hostName, 'Coworker'),
          guestName: cleanName(msg!.guestName, nameRef.current),
        };
        matchRef.current = match;
        snapRef.current = null;
        guestYRef.current = null;
        setOpponentName(match.hostName);
        setLostReason(null);
        setLink('connecting');
        setPhase('guest');
        return;
      }

      const match = matchRef.current;
      if (!match || typeof msg!.matchId !== 'string' || msg!.matchId !== match.matchId) return;

      if (kind === 'ready' && phaseRef.current === 'host' && from === match.guestId) {
        lastPacketRef.current = performance.now();
        if (linkRef.current === 'connecting') {
          setLink('live');
          celebrate();
        }
        return;
      }

      if (kind === 'input' && phaseRef.current === 'host' && from === match.guestId) {
        const y = msg!.y;
        if (typeof y !== 'number' || !Number.isFinite(y)) return;
        guestYRef.current = Math.max(0, Math.min(TABLE_H, y));
        lastPacketRef.current = performance.now();
        if (linkRef.current === 'connecting') {
          setLink('live');
          celebrate();
        }
        return;
      }

      if (kind === 'state' && phaseRef.current === 'guest' && from === match.hostId) {
        if (!isPongSnapshot(msg!.snap)) return;
        const prev = snapRef.current;
        if (prev && msg!.snap.seq <= prev.seq) return;
        snapRef.current = msg!.snap;
        lastPacketRef.current = performance.now();
        if (linkRef.current === 'connecting') {
          setLink('live');
          celebrate();
        }
        return;
      }

      if (kind === 'rematch' && phaseRef.current === 'host' && from === match.guestId) {
        setRematchNonce((n) => n + 1);
        return;
      }

      if (kind === 'leave' && (from === match.hostId || from === match.guestId)) {
        if (phaseRef.current === 'host' || phaseRef.current === 'guest') {
          setLostReason('left');
          setLink('lost');
        }
      }
    });
  }, [subscribePong]);

  // Drop seekers who stopped advertising.
  useEffect(() => {
    const t = window.setInterval(() => {
      const now = performance.now();
      setSeekers((prev) => {
        const next = prev.filter((s) => now - s.receivedAt < SEEK_TTL_MS);
        return next.length === prev.length ? prev : next;
      });
    }, 500);
    return () => window.clearInterval(t);
  }, []);

  // Advertise while waiting, and leave the queue after walking away.
  useEffect(() => {
    if (phase !== 'waiting') return;
    const id = userId;
    const tableId = waitingTableRef.current;
    if (!id || !tableId) return;
    if (near?.id !== tableId) {
      sendRef.current({ kind: 'cancel', tableId, userId: id });
      waitingTableRef.current = null;
      setPhase('idle');
      return;
    }
    const send = () => {
      sendRef.current({
        kind: 'seek',
        tableId,
        userId: id,
        name: nameRef.current,
      });
    };
    send();
    const t = window.setInterval(send, 700);
    return () => {
      window.clearInterval(t);
      // Leave the queue once a match starts or the player cancels, so the
      // table doesn't keep looking occupied.
      sendRef.current({ kind: 'cancel', tableId, userId: id });
    };
  }, [phase, near, userId, sendPong]);

  // Pair with the other waiter. The lower user id hosts so both clients agree.
  useEffect(() => {
    if (phase !== 'waiting' || !userId || !near) return;
    const tableId = waitingTableRef.current;
    if (!tableId || near.id !== tableId) return;
    const others = seekers.filter((s) => s.tableId === tableId && s.userId !== userId);
    if (others.length === 0) return;
    const ids = [userId, ...others.map((s) => s.userId)].sort();
    if (ids[0] !== userId || !ids[1]) return;
    const guest = others.find((s) => s.userId === ids[1]);
    if (!guest) return;
    const key = `${userId}:${guest.userId}:${tableId}`;
    if (offerRef.current === key) return;
    offerRef.current = key;
    const match: MatchInfo = {
      matchId: `${userId}:${guest.userId}:${Date.now()}`,
      tableId,
      hostId: userId,
      guestId: guest.userId,
      hostName: name,
      guestName: guest.name,
    };
    matchRef.current = match;
    const payload: StartPayload = { kind: 'start', ...match };
    startPayloadRef.current = payload;
    sendPong(payload);
    setOpponentName(guest.name);
    setLostReason(null);
    setLink('connecting');
    setPhase('host');
  }, [phase, near, seekers, userId, name, sendPong]);

  // Host repeats the invite until the guest answers. Guest repeats ready.
  useEffect(() => {
    if (link !== 'connecting') return;
    const id = userId;
    if (!id) return;
    if (phase === 'host') {
      const payload = startPayloadRef.current;
      if (!payload) return;
      const t = window.setInterval(() => sendRef.current(payload), 450);
      return () => window.clearInterval(t);
    }
    if (phase === 'guest') {
      const match = matchRef.current;
      if (!match) return;
      const send = () => sendRef.current({ kind: 'ready', matchId: match.matchId, userId: id });
      send();
      const t = window.setInterval(send, 400);
      return () => window.clearInterval(t);
    }
    return;
  }, [link, phase, userId]);

  // Give up if the other person never arrives, or if a live rally goes quiet.
  useEffect(() => {
    if (phase !== 'host' && phase !== 'guest') return;
    if (link === 'connecting') {
      const t = window.setTimeout(() => {
        if (linkRef.current === 'connecting') {
          setLostReason('timeout');
          setLink('lost');
        }
      }, CONNECT_TIMEOUT_MS);
      return () => window.clearTimeout(t);
    }
    if (link === 'live') {
      lastPacketRef.current = performance.now();
      const t = window.setInterval(() => {
        if (performance.now() - lastPacketRef.current > LIVE_TIMEOUT_MS) {
          setLostReason('left');
          setLink('lost');
        }
      }, 1000);
      return () => window.clearInterval(t);
    }
    return;
  }, [link, phase]);

  useEffect(() => {
    return () => {
      const id = userIdRef.current;
      const match = matchRef.current;
      const current = phaseRef.current;
      if (!id) return;
      if (match && (current === 'host' || current === 'guest')) {
        sendRef.current({ kind: 'leave', matchId: match.matchId, userId: id });
      } else if (current === 'waiting' && waitingTableRef.current) {
        sendRef.current({ kind: 'cancel', tableId: waitingTableRef.current, userId: id });
      }
    };
  }, []);

  const waitingHere = seekers.filter((s) => s.tableId === near?.id).map((s) => s.name);

  const youSide: 'left' | 'right' = phase === 'guest' ? 'right' : 'left';
  const leftName = phase === 'guest' ? (opponentName ?? 'Coworker') : name || 'You';
  const rightName =
    phase === 'single'
      ? 'Computer'
      : phase === 'guest'
        ? name || 'You'
        : (opponentName ?? 'Coworker');

  return {
    phase,
    link,
    lostReason,
    blocking: phase === 'single' || phase === 'host' || phase === 'guest',
    near,
    setNear,
    waitingHere,
    opponentName,
    leftName,
    rightName,
    youSide,
    guestYRef,
    snapRef,
    rematchNonce,
    startSingle,
    startDual,
    close,
    playSoloInstead,
    keepWaiting,
    publish,
    sendPaddle,
    requestRematch,
  };
}
